import React, { useEffect, useState } from 'react';
import { formatCurrency } from '../utils/formatCurrency';
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  createSubcategory,
  updateSubcategory,
  deleteSubcategory
} from '../api';

function formatMonthly(target, startDate, targetDate) {
  const start = new Date(startDate);
  const end = new Date(targetDate);
  const months = Math.max(1,
    (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())
  );
  return formatCurrency(Math.ceil(Number(target) / months));
}

export default function Categories({ onUpdated }) {

  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [newSub, setNewSub] = useState({});
  const [collapsedCats, setCollapsedCats] = useState(new Set());

  const toggleCat = (id) => {
    setCollapsedCats(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const loadCategories = async (collapseAll = false) => {
    try {
      const res = await getCategories();
      setCategories(res.data);
      if (collapseAll) setCollapsedCats(new Set(res.data.map(c => c.id)));
    } catch (err) {
      console.error("Erreur chargement catégories:", err);
    }
  };

  useEffect(() => { loadCategories(true); }, []);

  // ================= CATEGORY CRUD =================

  const handleCreateCategory = async () => {
    if (!newCategory) return;
    await createCategory({ name: newCategory });
    setNewCategory("");
    loadCategories();
    onUpdated?.();
  };

  const handleRenameCategory = async (id, name) => {
    const newName = prompt("Nouveau nom catégorie :", name);
    if (!newName) return;
    await updateCategory(id, { name: newName });
    loadCategories();
    onUpdated?.();
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("Supprimer la catégorie ?")) return;
    await deleteCategory(id);
    loadCategories();
    onUpdated?.();
  };

  // ================= SUBCATEGORY CRUD =================

  const handleCreateSub = async (categoryId) => {
    const name = newSub[categoryId];
    if (!name) return;

    await createSubcategory({ name, category_id: categoryId });
    setNewSub(prev => ({ ...prev, [categoryId]: "" }));
    loadCategories();
    onUpdated?.();
  };

  const handleRenameSub = async (id, name, category_id) => {
    const newName = prompt("Nouveau nom sous-catégorie :", name);
    if (!newName) return;

    await updateSubcategory(id, { name: newName, category_id });
    loadCategories();
    onUpdated?.();
  };

  const handleDeleteSub = async (id) => {
    if (!window.confirm("Supprimer la sous-catégorie ?")) return;
    await deleteSubcategory(id);
    loadCategories();
    onUpdated?.();
  };

  // ================= RENDER =================

  const allCatIds = categories.map(c => c.id);
  const allCollapsed = allCatIds.length > 0 && allCatIds.every(id => collapsedCats.has(id));

  return (
    <div style={{ marginTop: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Catégories</h2>
        <button
          onClick={() => setCollapsedCats(allCollapsed ? new Set() : new Set(allCatIds))}
          style={{ fontSize: 12, padding: '4px 10px', cursor: 'pointer', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text-soft)' }}
        >
          {allCollapsed ? 'Tout développer' : 'Tout réduire'}
        </button>
      </div>

      <div style={{ marginBottom: 20 }}>
        <input
          placeholder="Nouvelle catégorie"
          value={newCategory}
          onChange={e => setNewCategory(e.target.value)}
        />
        <button onClick={handleCreateCategory}>Ajouter</button>
      </div>

      {categories.map(cat => {
        const isCollapsed = collapsedCats.has(cat.id);
        return (
        <div key={cat.id} style={{ marginBottom: 30 }}>

          <h3
            onClick={() => toggleCat(cat.id)}
            style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 12 }}>{isCollapsed ? '▸' : '▾'}</span>
            {cat.name}
            {isCollapsed && (
              <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>
                — {cat.subcategories.length} sous-catégorie{cat.subcategories.length > 1 ? 's' : ''}
              </span>
            )}
            <span onClick={e => e.stopPropagation()} style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
              <button onClick={() => handleRenameCategory(cat.id, cat.name)}>✏️</button>
              <button onClick={() => handleDeleteCategory(cat.id)}>🗑️</button>
            </span>
          </h3>

          {!isCollapsed && <ul>
            {cat.subcategories.map(sub => (
              <li key={sub.id} style={{ marginBottom: 8 }}>
                <b>{sub.name}</b>{" "}
                <button onClick={() => handleRenameSub(sub.id, sub.name, cat.id)}>✏️</button>
                <button onClick={() => handleDeleteSub(sub.id)}>🗑️</button>

                <div style={{ marginTop: 5 }}>
                  <label>Méthode budget : </label>

                  <select
                    value={sub.budget_method || "average"}
                    onChange={async (e) => {
                      const newMethod = e.target.value;
                      setCategories(prev =>
                        prev.map(c => ({
                          ...c,
                          subcategories: c.subcategories.map(s =>
                            s.id === sub.id ? { ...s, budget_method: newMethod } : s
                          )
                        }))
                      );
                      await updateSubcategory(sub.id, { budget_method: newMethod });
                      loadCategories();
                      onUpdated?.();
                    }}
                  >
                    <option value="smart">Moyenne auto (détectée)</option>
                    <option value="average">Moyenne (N mois manuel)</option>
                    <option value="annualized">Annualisé (12 mois)</option>
                    <option value="fixed">Montant fixe</option>
                    <option value="saving">Objectif d'épargne</option>
                    <option value="provision">Provision (lissage)</option>
                    <option value="loan">Remboursement prêt</option>
                    <option value="none">Ignoré</option>
                  </select>

                  {sub.budget_method === "average" && (
                    <>
                      <span style={{ marginLeft: 10 }}>Mois :</span>
                      <input
                        type="number"
                        value={sub.budget_months || 6}
                        style={{ width: 60, marginLeft: 5 }}
                        onChange={async (e) => {
                          const newMonths = Number(e.target.value);
                          setCategories(prev =>
                            prev.map(c => ({
                              ...c,
                              subcategories: c.subcategories.map(s =>
                                s.id === sub.id ? { ...s, budget_months: newMonths } : s
                              )
                            }))
                          );
                          await updateSubcategory(sub.id, { budget_months: newMonths });
                          loadCategories();
                        }}
                      />
                    </>
                  )}

                  {sub.budget_method === "saving" && (
                    <div style={{ marginTop: 6, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <label style={{ fontSize: 13 }}>
                        Cible (€) :
                        <input
                          type="number"
                          min="1"
                          value={sub.saving_target || ""}
                          placeholder="3000"
                          style={{ width: 90, marginLeft: 5 }}
                          onChange={e =>
                            setCategories(prev =>
                              prev.map(c => ({
                                ...c,
                                subcategories: c.subcategories.map(s =>
                                  s.id === sub.id ? { ...s, saving_target: e.target.value } : s
                                )
                              }))
                            )
                          }
                          onBlur={async (e) => {
                            await updateSubcategory(sub.id, { saving_target: e.target.value || null });
                            onUpdated?.();
                          }}
                        />
                      </label>
                      <label style={{ fontSize: 13 }}>
                        Date de début :
                        <input
                          type="date"
                          value={sub.saving_start_date ? sub.saving_start_date.slice(0, 10) : ""}
                          style={{ marginLeft: 5 }}
                          onChange={e =>
                            setCategories(prev =>
                              prev.map(c => ({
                                ...c,
                                subcategories: c.subcategories.map(s =>
                                  s.id === sub.id ? { ...s, saving_start_date: e.target.value } : s
                                )
                              }))
                            )
                          }
                          onBlur={async (e) => {
                            await updateSubcategory(sub.id, { saving_start_date: e.target.value || null });
                            onUpdated?.();
                          }}
                        />
                      </label>
                      <label style={{ fontSize: 13 }}>
                        Date limite :
                        <input
                          type="date"
                          value={sub.saving_target_date ? sub.saving_target_date.slice(0, 10) : ""}
                          style={{ marginLeft: 5 }}
                          onChange={e =>
                            setCategories(prev =>
                              prev.map(c => ({
                                ...c,
                                subcategories: c.subcategories.map(s =>
                                  s.id === sub.id ? { ...s, saving_target_date: e.target.value } : s
                                )
                              }))
                            )
                          }
                          onBlur={async (e) => {
                            await updateSubcategory(sub.id, { saving_target_date: e.target.value || null });
                            onUpdated?.();
                          }}
                        />
                      </label>
                      {sub.saving_target && sub.saving_start_date && sub.saving_target_date && (
                        <span style={{ fontSize: 12, color: "#2e7d32" }}>
                          → {formatMonthly(sub.saving_target, sub.saving_start_date, sub.saving_target_date)}/mois
                        </span>
                      )}
                    </div>
                  )}

                  {sub.budget_method === "loan" && (
                    <div style={{ marginTop: 5, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 13 }}>Capital :</span>
                      <input
                        type="number" min="0" step="1" placeholder="ex: 3000"
                        value={sub.saving_target || ""}
                        style={{ width: 90, marginLeft: 5 }}
                        onChange={e => setCategories(prev => prev.map(c => ({ ...c, subcategories: c.subcategories.map(s => s.id === sub.id ? { ...s, saving_target: e.target.value } : s) })))}
                        onBlur={async e => { await updateSubcategory(sub.id, { saving_target: e.target.value || null }); }}
                      />
                      <span style={{ fontSize: 13 }}>Depuis :</span>
                      <input
                        type="date"
                        value={sub.saving_start_date ? sub.saving_start_date.slice(0, 10) : ""}
                        style={{ marginLeft: 5 }}
                        onChange={e => setCategories(prev => prev.map(c => ({ ...c, subcategories: c.subcategories.map(s => s.id === sub.id ? { ...s, saving_start_date: e.target.value } : s) })))}
                        onBlur={async e => { await updateSubcategory(sub.id, { saving_start_date: e.target.value || null }); }}
                      />
                      <span style={{ fontSize: 13 }}>Fin :</span>
                      <input
                        type="date"
                        value={sub.saving_target_date ? sub.saving_target_date.slice(0, 10) : ""}
                        style={{ marginLeft: 5 }}
                        onChange={e => setCategories(prev => prev.map(c => ({ ...c, subcategories: c.subcategories.map(s => s.id === sub.id ? { ...s, saving_target_date: e.target.value } : s) })))}
                        onBlur={async e => { await updateSubcategory(sub.id, { saving_target_date: e.target.value || null }); }}
                      />
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>}

          {!isCollapsed && <>
            <input
              placeholder="Nouvelle sous-catégorie"
              value={newSub[cat.id] || ""}
              onChange={e =>
                setNewSub(prev => ({ ...prev, [cat.id]: e.target.value }))
              }
            />
            <button onClick={() => handleCreateSub(cat.id)}>
              Ajouter sous-catégorie
            </button>
          </>}

          <hr />
        </div>
        );
      })}
    </div>
  );
}
